import { useReducer } from 'react';

// Inlined from the shallow-equal package (frozen upstream); its
// null-input branches are dropped — both args are non-null objects by type.
// The Map bridges string keys to value reads without an index signature; its
// has() keeps the upstream hasOwnProperty semantics (an undefined-valued key
// is not the same as a missing one).
function shallowEqualObjects(objA: object, objB: object): boolean {
  const bValues = new Map<string, unknown>();
  for (const [key, value] of Object.entries(objB)) {
    bValues.set(key, value);
  }
  const aEntries = Object.entries(objA);
  if (aEntries.length !== bValues.size) {
    return false;
  }
  return aEntries.every(
    ([key, value]) => bValues.has(key) && bValues.get(key) === value,
  );
}

function reducer<T extends object, K extends T>(
  oldState: T,
  newState: K | ((arg: T) => K | null),
): T {
  if (typeof newState === 'function') {
    const next = newState(oldState);
    if (next === null) {
      return oldState;
    }
    newState = next;
  }
  if (shallowEqualObjects(oldState, newState)) {
    return oldState;
  }
  return { ...oldState, ...newState };
}

type AtLeastOne<T> = { [K in keyof T]: Pick<T, K> }[keyof T] & Partial<T>;

// useState for object state with setState-style partial merging: the setter
// takes a patch holding at least one existing key and merges it over the
// current state. Render stability: the shallow-equal guard compares the
// PATCH against the whole current state, so only a full-state-identical
// patch keeps the same reference (no re-render) — a value-identical PARTIAL
// patch still produces a new state object. A functional updater receives
// the current state and may return null to abort the update entirely (same
// reference, no re-render). The state SHAPE is fixed at init: patches are
// typed to existing keys; a key the types were bypassed on still merges.
export function useStateReducer<T extends object, K extends AtLeastOne<T>>(
  iState: T,
): [T, (arg: K | ((arg1: T) => K | null)) => void] {
  const [state, setState] = useReducer(reducer, iState);
  return [state, setState];
}
